import { useState, useRef, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
    FiUser, FiSettings, FiLock, FiAward, FiSun, FiMoon,
    FiCamera, FiSave, FiDownload, FiCheckCircle
} from 'react-icons/fi';
import api, { RUTA_ARCHIVOS } from '../api/client';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { Avatar } from '../components/Layout';

export default function Perfil() {
    const { usuario, actualizarUsuario } = useAuth();
    const { tema, cambiarTema } = useTheme();
    const location = useLocation();
    const navigate = useNavigate();
    const inputArchivo = useRef(null);

    const tabActual = location.pathname.includes('configuracion') ? 'config'
        : location.pathname.includes('seguridad') ? 'seguridad'
        : location.pathname.includes('certificados') ? 'certificados'
        : 'perfil';

    const [datos, setDatos] = useState({
        nombre: usuario?.nombre || '',
        apellido_paterno: usuario?.apellido_paterno || '',
        apellido_materno: usuario?.apellido_materno || ''
    });
    const [mensaje, setMensaje] = useState({ tipo: '', texto: '' });
    const [pwd, setPwd] = useState({ contrasenaActual: '', nuevaContrasena: '', confirmar: '' });
    const [certificados, setCertificados] = useState([]);

    useEffect(() => {
        if (tabActual === 'certificados') {
            api.get('/usuarios/certificados').then(r => setCertificados(r.data.certificados || []));
        }
    }, [tabActual]);

    const guardarPerfil = async (e) => {
        e.preventDefault();
        try {
            await api.put('/usuarios/perfil', datos);
            actualizarUsuario(datos);
            setMensaje({ tipo: 'exito', texto: 'Perfil actualizado' });
        } catch (err) {
            setMensaje({ tipo: 'error', texto: err.response?.data?.error || 'Error' });
        }
    };

    const subirAvatar = async (e) => {
        const archivo = e.target.files?.[0];
        if (!archivo) return;
        const fd = new FormData();
        fd.append('avatar', archivo);
        try {
            const res = await api.post('/usuarios/avatar', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
            actualizarUsuario({ imagen_perfil: res.data.imagen_perfil });
            setMensaje({ tipo: 'exito', texto: 'Foto actualizada' });
        } catch (err) {
            setMensaje({ tipo: 'error', texto: 'Error subiendo foto' });
        }
    };

    const cambiarPwd = async (e) => {
        e.preventDefault();
        setMensaje({ tipo: '', texto: '' });
        if (pwd.nuevaContrasena !== pwd.confirmar) {
            setMensaje({ tipo: 'error', texto: 'Las contrasenas no coinciden' });
            return;
        }
        try {
            await api.post('/usuarios/cambiar-contrasena', {
                contrasenaActual: pwd.contrasenaActual,
                nuevaContrasena: pwd.nuevaContrasena
            });
            setMensaje({ tipo: 'exito', texto: 'Contrasena cambiada correctamente' });
            setPwd({ contrasenaActual: '', nuevaContrasena: '', confirmar: '' });
        } catch (err) {
            setMensaje({ tipo: 'error', texto: err.response?.data?.error || 'Error' });
        }
    };

    return (
        <div>
            <div className="perfil-header">
                <div style={{ position: 'relative' }}>
                    <Avatar usuario={usuario} tamano="lg" />
                    <button
                        className="icon-btn"
                        onClick={() => inputArchivo.current?.click()}
                        style={{ position: 'absolute', bottom: 0, right: 0, background: 'var(--acento)', color: 'white' }}
                        title="Cambiar foto"
                    >
                        <FiCamera size={16} />
                    </button>
                    <input
                        ref={inputArchivo}
                        type="file"
                        accept="image/*"
                        style={{ display: 'none' }}
                        onChange={subirAvatar}
                    />
                </div>
                <div style={{ flex: 1 }}>
                    <h2 style={{ margin: '0 0 4px', fontSize: 22 }}>
                        {usuario?.nombre} {usuario?.apellido_paterno} {usuario?.apellido_materno}
                    </h2>
                    <div style={{ color: 'var(--texto-suave)', fontSize: 14 }}>{usuario?.correo}</div>
                    <div style={{ color: 'var(--acento)', fontSize: 13, marginTop: 4, textTransform: 'capitalize' }}>
                        {usuario?.rol === 'administrador' ? 'Administrador' : 'Estudiante'}
                    </div>
                </div>
            </div>

            <div className="perfil-grid">
                <div className="perfil-tabs">
                    <button className={tabActual === 'perfil' ? 'activa' : ''} onClick={() => navigate('/perfil')}>
                        <FiUser /> Perfil
                    </button>
                    <button className={tabActual === 'config' ? 'activa' : ''} onClick={() => navigate('/perfil/configuracion')}>
                        <FiSettings /> Configuracion
                    </button>
                    <button className={tabActual === 'seguridad' ? 'activa' : ''} onClick={() => navigate('/perfil/seguridad')}>
                        <FiLock /> Seguridad
                    </button>
                    <button className={tabActual === 'certificados' ? 'activa' : ''} onClick={() => navigate('/perfil/certificados')}>
                        <FiAward /> Certificados
                    </button>
                </div>

                <div>
                    {mensaje.texto && (
                        <div className={mensaje.tipo === 'exito' ? 'exito-msg' : 'error-msg'}>{mensaje.texto}</div>
                    )}

                    {tabActual === 'perfil' && (
                        <div className="card">
                            <h3>Mis Datos</h3>
                            <form onSubmit={guardarPerfil}>
                                <div className="form-group">
                                    <label>Nombre</label>
                                    <input className="input" value={datos.nombre} onChange={e => setDatos({ ...datos, nombre: e.target.value })} />
                                </div>
                                <div className="form-group">
                                    <label>Apellido Paterno</label>
                                    <input className="input" value={datos.apellido_paterno} onChange={e => setDatos({ ...datos, apellido_paterno: e.target.value })} />
                                </div>
                                <div className="form-group">
                                    <label>Apellido Materno</label>
                                    <input className="input" value={datos.apellido_materno} onChange={e => setDatos({ ...datos, apellido_materno: e.target.value })} />
                                </div>
                                <div className="form-group">
                                    <label>Correo electronico</label>
                                    <input className="input" value={usuario?.correo || ''} disabled />
                                </div>
                                <button type="submit" className="btn btn-primario"><FiSave /> Guardar cambios</button>
                            </form>
                        </div>
                    )}

                    {tabActual === 'config' && (
                        <div className="card">
                            <h3>Apariencia</h3>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <div>
                                    <div style={{ fontWeight: 500 }}>Tema {tema === 'oscuro' ? 'oscuro' : 'claro'}</div>
                                    <div style={{ color: 'var(--texto-suave)', fontSize: 13 }}>
                                        Cambia entre tema claro y oscuro
                                    </div>
                                </div>
                                <button className="btn btn-fantasma" onClick={cambiarTema}>
                                    {tema === 'oscuro' ? <FiSun /> : <FiMoon />}
                                    Cambiar a {tema === 'oscuro' ? 'claro' : 'oscuro'}
                                </button>
                            </div>
                        </div>
                    )}

                    {tabActual === 'seguridad' && (
                        <div className="card">
                            <h3>Cambiar Contrasena</h3>
                            <form onSubmit={cambiarPwd}>
                                <div className="form-group">
                                    <label>Contrasena actual</label>
                                    <input
                                        type="password"
                                        className="input"
                                        value={pwd.contrasenaActual}
                                        onChange={e => setPwd({ ...pwd, contrasenaActual: e.target.value })}
                                    />
                                </div>
                                <div className="form-group">
                                    <label>Nueva contrasena</label>
                                    <input
                                        type="password"
                                        className="input"
                                        value={pwd.nuevaContrasena}
                                        onChange={e => setPwd({ ...pwd, nuevaContrasena: e.target.value })}
                                    />
                                </div>
                                <div className="form-group">
                                    <label>Confirmar nueva contrasena</label>
                                    <input
                                        type="password"
                                        className="input"
                                        value={pwd.confirmar}
                                        onChange={e => setPwd({ ...pwd, confirmar: e.target.value })}
                                    />
                                </div>
                                <button type="submit" className="btn btn-primario">Actualizar contrasena</button>
                            </form>
                        </div>
                    )}

                    {tabActual === 'certificados' && (
                        <div className="card">
                            <h3>Mis Certificados</h3>
                            {certificados.length === 0 ? (
                                <p style={{ color: 'var(--texto-suave)' }}>
                                    Aun no tienes certificados. Completa un curso y califica para obtener uno.
                                </p>
                            ) : (
                                <table className="tabla">
                                    <thead>
                                        <tr>
                                            <th>Curso</th>
                                            <th>Profesor</th>
                                            <th>Fecha</th>
                                            <th>Codigo</th>
                                            <th></th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {certificados.map(c => (
                                            <tr key={c.id}>
                                                <td><FiCheckCircle style={{ color: 'var(--exito)', marginRight: 6 }} />{c.curso_nombre}</td>
                                                <td>{c.profesor_nombre || '-'}</td>
                                                <td>{new Date(c.fecha_emision).toLocaleDateString('es-MX')}</td>
                                                <td style={{ fontFamily: 'monospace', fontSize: 12 }}>{c.codigo_certificado}</td>
                                                <td>
                                                    <a className="btn btn-fantasma" href={`${RUTA_ARCHIVOS}${c.archivo_url}`} target="_blank" rel="noreferrer">
                                                        <FiDownload /> PDF
                                                    </a>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            )}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}
